import { useId } from 'react'
import { useTranslation } from 'react-i18next'
import { WarningAmberOutlined } from '@mui/icons-material'
import {
  Button,
  darken,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
} from '@mui/material'

import { TILE_RADIUS_PX } from '@/common/constants'
import { CHART_CARD_TITLE_SX } from '@/common/styles'

import type { PolicyInfoModalProps } from '.'

/**
 * The titles from `getModalText` are in capitals, for the old dialog. The new theme shows titles
 * in sentence case. A title with a lowercase letter, such as "Delete Downtown cap?", has a
 * policy name in it, so it stays as it is.
 */
const toSentenceCase = (title: string): string =>
  title === title.toLocaleUpperCase() ? title.charAt(0) + title.slice(1).toLocaleLowerCase() : title

/**
 * The new Mobility theme look of `PolicyInfoModal`: the same MUI dialog as `ThemedCreateModal`.
 * It takes the same props, so the controllers do not change.
 *
 * With `onCancel`, it asks the user to confirm an action: Cancel and Confirm. Without it, it only
 * gives information, such as an error: one OK button, which runs `onConfirm`. The controllers
 * make `onConfirm` close the dialog when the caller does not give one.
 */
export const ThemedPolicyInfoModal = ({
  onCancel,
  onConfirm,
  text,
  title,
}: PolicyInfoModalProps) => {
  const { t } = useTranslation()
  const titleId = useId()
  const textId = useId()

  const asksToConfirm = !!onCancel

  return (
    <Dialog
      open
      // Escape and a click outside do what Cancel does. A dialog without Cancel only informs,
      // so they close it the same way as its OK button.
      onClose={onCancel ?? onConfirm}
      fullWidth
      maxWidth="xs"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={text ? textId : undefined}
      slotProps={{
        paper: {
          // A confirmation interrupts the user and needs an answer, so it is an alert dialog.
          role: asksToConfirm ? 'alertdialog' : 'dialog',
          sx: { borderRadius: `${TILE_RADIUS_PX}px` },
        },
      }}
    >
      {title && (
        <DialogTitle id={titleId} sx={[CHART_CARD_TITLE_SX, { pt: 3 }]}>
          {/* A `span`: the title is an `h2`, which must not hold a `div`. */}
          <Stack component="span" direction="row" alignItems="center" gap={1}>
            <WarningAmberOutlined color="warning" aria-hidden />
            {toSentenceCase(title)}
          </Stack>
        </DialogTitle>
      )}
      {text && (
        <DialogContent>
          <DialogContentText id={textId} color="text.primary">
            {text}
          </DialogContentText>
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, pb: 3 }}>
        {onCancel && (
          // `primary.main` text on white is 3.2:1, below WCAG AA. `primary.dark` is 6:1.
          <Button onClick={onCancel} sx={{ color: 'primary.dark' }}>
            {t('common.cancelTitle', 'Cancel')}
          </Button>
        )}
        {onConfirm && (
          <Button
            onClick={onConfirm}
            variant="contained"
            // White on `primary.main` is 3.2:1, below WCAG AA. On `primary.dark` it is 6:1.
            sx={{
              bgcolor: 'primary.dark',
              '&:hover': { bgcolor: theme => darken(theme.palette.primary.dark, 0.2) },
            }}
          >
            {asksToConfirm ? t('common.confirmTitle', 'Confirm') : t('common.ok', 'OK')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  )
}
